import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { EntityType } from '../domain/entityTypes.js';
import { useTRPC, useTRPCClient } from '../utils/trpc.js';
import { ENTITY_CALLS, type AdoptToBaseRequest } from './entityCalls.js';

export type AdoptToMasterTarget = {
    type: EntityType;
    /** The wire shape, with no UI label mixed in. */
    request: AdoptToBaseRequest;
    /** The field's label, as the diff names it. */
    fieldLabel: string;
    /** The record's display label. */
    recordLabel: string;
};

/**
 * **Adopt to master**'s query (the dry run) and mutation, dispatched through `ENTITY_CALLS[type]`
 * so the dialog reads one type-agnostic shape rather than a `'preview' | 'write'` flag over a
 * hand-written query key. **Adopt as override** only ever touches one environment; this changes master's
 * default for every environment, so it opens on a dry run and lists what happens where before anything
 * moves. Confirming writes master only; the push that follows is its own step.
 */
export const useAdoptToMaster = (target: AdoptToMasterTarget, onDone: () => void) => {
    const trpc = useTRPC();
    const client = useTRPCClient();
    const { type, request, fieldLabel } = target;
    const calls = ENTITY_CALLS[type];

    const preview = useQuery({
        ...calls.adoptToBasePreviewOptions(trpc, request),
        meta: { quiet: true },
        retry: false,
    });
    const write = useMutation({
        mutationFn: (r: AdoptToBaseRequest) => calls.adoptToBase(client, r),
        meta: { quiet: true },
        onSuccess: () => {
            notifications.show({ message: `Adopted ${request.environment}'s ${fieldLabel} to master`, color: 'green' });
            onDone();
        },
    });

    return { preview, write, request };
};
